/**
 * P440 -- the MVB target state.
 *
 * What it would take for the minimum viable bank to meet every horizon
 * the bank has set for it, what that costs, and in what order: missed
 * recovery horizons first, then continuity postures, then technology
 * out of support. A step with no cost says so and the total then reads
 * "at least"; a gap no step can close says why.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api, type ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatSAR } from "shared/money/money";

import { AdoptedTargetPanel } from "./AdoptedTargetPanel";

const P = "targetState";

interface StateSummary {
  time_to_minimum_viability_hours: number;
  horizons: { horizon: string; met: boolean }[];
  missed: string[];
}

interface Step {
  kind: "recovery" | "posture" | "technology" | "supplier";
  target: string;
  cost_sar: string | null;
  closes: string[];
  spent_sar: string;
  from_rto_hours?: number | null;
  to_rto_hours?: number;
  estimated?: boolean;
  items?: number;
  moves?: number;
  services?: string[];
  after?: StateSummary;
}

interface Tier {
  kind: "recovery" | "posture" | "technology" | "supplier";
  gaps: number;
  steps: Step[];
  closed_without_cost: string[];
  open: { name: string; reason: string; teams?: string }[];
  not_bought?: { target: string; to_rto_hours: string; cost_sar: string }[];
}

export interface TargetState {
  nothing_to_plan: "no_mvb_declared" | "no_gaps" | null;
  budget_sar: string | null;
  before?: StateSummary;
  after?: StateSummary;
  tiers: Tier[];
  total_sar?: string;
  unpriced_steps?: number;
  at_least?: boolean;
}

function Cost({ value }: { value: string | null }) {
  const { t } = useTranslation();
  return value === null ? (
    <span className="text-amber-800">{t(`${P}.needsCost`)}</span>
  ) : (
    <span>{formatSAR(value)}</span>
  );
}

function StepLine({ s }: { s: Step }) {
  const { t } = useTranslation();
  const what =
    s.kind === "recovery"
      ? t(`${P}.step.recovery`, {
          target: s.target,
          from: s.from_rto_hours ?? "?",
          to: s.to_rto_hours ?? "?",
        })
      : s.kind === "posture"
        ? t(`${P}.step.posture`, { target: s.target })
        : s.kind === "supplier"
          ? t(`${P}.step.supplier`, {
              count: s.moves ?? 0,
              target: s.target,
              services: (s.services ?? []).join(", "),
            })
          : t(`${P}.step.technology`, { count: s.items ?? 0, target: s.target });
  return (
    <li
      data-testid="target-step"
      data-kind={s.kind}
      className="space-y-0.5 rounded border border-slate-200 bg-white p-2 text-sm"
    >
      <p className="flex flex-wrap items-center gap-2">
        <span className="text-slate-900">{what}</span>
        <span className="text-xs text-slate-600">
          <Cost value={s.cost_sar} />
        </span>
        {s.estimated && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
            {t(`${P}.estimate`)}
          </span>
        )}
      </p>
      {s.kind === "recovery" && s.after && (
        <p className="text-xs text-slate-600">
          {s.closes.length > 0
            ? t(`${P}.closesAndAfter`, {
                closes: s.closes.join(", "),
                hours: s.after.time_to_minimum_viability_hours,
              })
            : t(`${P}.after`, { hours: s.after.time_to_minimum_viability_hours })}
        </p>
      )}
    </li>
  );
}

export function TargetStateView() {
  const { t } = useTranslation();
  const [budget, setBudget] = useState("");
  const [applied, setApplied] = useState<string | null>(null);
  const q = useQuery<TargetState, ApiError>({
    queryKey: ["twin", "mvb-target-state", applied],
    queryFn: () =>
      api.get<TargetState>(
        `/v1/twin/mvb/target-state${applied ? `?budget_sar=${encodeURIComponent(applied)}` : ""}`,
      ),
  });
  const data = q.data && Array.isArray(q.data.tiers) ? q.data : null;
  return (
    <article className="space-y-4" data-testid="target-state-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 font-display text-2xl text-slate-900">
          <MarsadIcon name="mvb" className="text-brand-brass" />
          {t(`${P}.heading`)}
        </h1>
        <p className="text-sm text-slate-600">{t(`${P}.standfirst`)}</p>
      </header>

      <form
        className="flex flex-wrap items-end gap-2 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(budget.trim() ? budget.trim() : null);
        }}
      >
        <label className="text-xs text-slate-600">
          {t(`${P}.budgetLabel`)}
          <input
            data-testid="target-budget"
            inputMode="decimal"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="mt-1 block w-40 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="submit"
          className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
        >
          {t(`${P}.apply`)}
        </button>
      </form>

      {q.isError && (
        <p role="alert" className="text-sm text-rose-800">
          {q.error.detail}
        </p>
      )}
      {data?.nothing_to_plan && (
        <p data-testid="target-nothing" className="text-sm text-slate-700">
          {t(`${P}.nothing.${data.nothing_to_plan}`)}
        </p>
      )}
      {data && !data.nothing_to_plan && data.before && data.after && (
        <>
          <section
            data-testid="target-summary"
            className="space-y-1 rounded border border-slate-200 bg-white p-3 text-sm"
          >
            <p>
              {t(`${P}.viability`, {
                before: data.before.time_to_minimum_viability_hours,
                after: data.after.time_to_minimum_viability_hours,
              })}
            </p>
            <p data-testid="target-total" className="font-medium text-slate-900">
              {data.at_least
                ? t(`${P}.totalAtLeast`, {
                    total: formatSAR(data.total_sar ?? "0"),
                    count: data.unpriced_steps ?? 0,
                  })
                : t(`${P}.total`, { total: formatSAR(data.total_sar ?? "0") })}
            </p>
            {data.after.missed.length > 0 && (
              <p className="text-xs text-rose-800">
                {t(`${P}.stillMissed`, { names: data.after.missed.join(", ") })}
              </p>
            )}
            <p className="text-xs text-slate-500">{t(`${P}.method`)}</p>
          </section>

          {data.tiers.map((tier) => (
            <section
              key={tier.kind}
              data-testid={`target-tier-${tier.kind}`}
              className="space-y-2"
            >
              <h2 className="text-sm font-semibold text-slate-900">
                {t(`${P}.tier.${tier.kind}`, { count: tier.gaps })}
              </h2>
              {tier.gaps === 0 && (
                <p className="text-xs text-slate-600">{t(`${P}.noGapsInTier`)}</p>
              )}
              {tier.steps.length > 0 && (
                <ol className="space-y-1">
                  {tier.steps.map((s, i) => (
                    <StepLine key={`${s.target}-${i}`} s={s} />
                  ))}
                </ol>
              )}
              {tier.closed_without_cost.length > 0 && (
                <p className="text-xs text-emerald-800">
                  {t(`${P}.closedFree`, { names: tier.closed_without_cost.join(", ") })}
                </p>
              )}
              {(tier.not_bought ?? []).length > 0 && (
                <ul
                  data-testid={`target-not-bought-${tier.kind}`}
                  className="space-y-0.5 text-xs text-slate-600"
                >
                  {(tier.not_bought ?? []).map((n) => (
                    <li key={n.target}>
                      {t(`${P}.notBought`, {
                        target: n.target,
                        to: n.to_rto_hours,
                        cost: n.cost_sar ? formatSAR(n.cost_sar) : t(`${P}.needsCost`),
                      })}
                    </li>
                  ))}
                </ul>
              )}
              {tier.open.length > 0 && (
                <ul
                  data-testid={`target-open-${tier.kind}`}
                  className="space-y-0.5 text-xs text-rose-800"
                >
                  {tier.open.map((o) => (
                    <li key={o.name}>
                      {t(`${P}.open.${o.reason}`, {
                        name: o.name,
                        teams: o.teams ?? "",
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <AdoptedTargetPanel budget={applied} />
          <p className="text-xs text-slate-500">
            <Link
              to="/twin/mvb-recovery"
              className="text-brand-brassDark hover:underline"
            >
              {t(`${P}.toRecovery`)}
            </Link>
          </p>
        </>
      )}
    </article>
  );
}
